@if (sidebarOpen()) {
  <button
    type="button"
    class="fixed inset-0 z-40 bg-black/50 lg:hidden"
    aria-label="Close navigation"
    (click)="closeSidebar()"
  ></button>
}

<div class="flex h-screen overflow-hidden">
  <aside
    role="navigation"
    aria-label="Main navigation"
    class="fixed inset-y-0 left-0 z-50 flex flex-col bg-neutral-900 text-neutral-300 transition-all duration-200 ease-in-out lg:static lg:z-auto"
    [class.w-64]="!sidebarCollapsed()"
    [class.w-18]="sidebarCollapsed()"
    [class.translate-x-0]="sidebarOpen()"
    [class.-translate-x-full]="!sidebarOpen()"
    [class.lg:translate-x-0]="true"
  >
    <app-admin-sidebar
      [collapsed]="sidebarCollapsed()"
      [username]="auth.username()"
      (navItemClick)="onNavItemClick()"
      (logoutClick)="onLogout()"
      (toggleCollapse)="toggleSidebar()"
    />
  </aside>

  <div class="flex-1 flex flex-col overflow-hidden min-w-0">
    <header
      class="h-14 bg-[var(--bg-card)] border-b border-[var(--border-default)] flex items-center shrink-0 px-4 lg:px-6 gap-4"
    >
      <button
        type="button"
        class="lg:hidden p-1 -ml-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        aria-label="Toggle sidebar"
        (click)="toggleMobileSidebar()"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            [attr.d]="sidebarOpen() ? icons.close : icons.hamburger"
          />
        </svg>
      </button>

      <nav
        data-testid="admin-breadcrumbs"
        class="hidden sm:flex items-center text-sm text-[var(--text-secondary)] min-w-0 shrink-0"
        aria-label="Breadcrumb"
      >
        <span class="text-[var(--text-muted)]">Admin</span>
        @for (crumb of breadcrumbs(); track crumb.path; let last = $last) {
          <svg
            class="w-4 h-4 mx-1.5 text-[var(--text-muted)] shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          @if (last) {
            <span class="text-[var(--text-primary)] font-medium truncate">
              {{ crumb.label | translate }}
            </span>
          } @else {
            <a
              [routerLink]="crumb.path"
              class="hover:text-[var(--text-primary)] transition-colors truncate"
            >
              {{ crumb.label | translate }}
            </a>
          }
        }
      </nav>

      <div class="flex-1"></div>

      <div class="relative max-w-xs w-full hidden md:block">
        <svg
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)] pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="icons.search" />
        </svg>
        <input
          #searchInput
          type="text"
          aria-label="Global search"
          class="w-full pl-9 pr-3 py-1.5 text-sm bg-[var(--bg-secondary)] border border-[var(--border-default)] rounded-none placeholder-[var(--text-muted)] text-[var(--text-primary)] focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
          [placeholder]="'header.searchPlaceholder' | translate"
          (keydown.enter)="onGlobalSearch($event)"
        />
      </div>

      <div class="flex items-center gap-1 shrink-0">
        @for (locale of translationService.availableLocales; track locale) {
          <button
            type="button"
            class="px-2 py-1 text-xs rounded-none font-heading font-medium tracking-[0.1px] transition-colors"
            [class]="
              translationService.locale() === locale
                ? 'bg-primary-500 text-white'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)]'
            "
            (click)="switchLocale(locale)"
          >
            {{ locale | uppercase }}
          </button>
        }
      </div>

      <div class="flex items-center pl-4 border-l border-[var(--border-default)] shrink-0">
        <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white text-xs font-bold"
          [attr.title]="auth.username()"
        >
          {{ userInitials() }}
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto p-6 bg-[var(--bg-page)]">
      <router-outlet />
    </main>
  </div>
</div>

<app-toast />
